import React, { useEffect } from "react";
import { useParams } from "react-router-dom";

import { Text, toast } from "@performyard/py-components";

import CompetencyForm from "./CompetencyForm";

import { useCompetency } from "../../../../api/hooks/defs/competencies";
import useNavigate from "../../../../hooks/useNavigate";

import { COMPETENCIES_LIST_PATH, CompetencyFormValues, EMPTY_FORM_VALUES, fromCompetency } from "./validationSchema";

export interface CreateEditCompetencyProps {
	mode: "create" | "edit";
}

const CreateEditCompetency: React.FC<CreateEditCompetencyProps> = ({ mode }) => {
	const navigate = useNavigate();
	const { competencyId = "" } = useParams<{ competencyId: string }>();
	const isEdit = mode === "edit";

	// useCompetency is always called (hook order must be stable) but is internally
	// gated on truthy id, so it no-ops in create mode.
	const competencyQuery = useCompetency(isEdit ? competencyId : "");
	const isLoading = isEdit && competencyQuery.isLoading;
	const loadError = isEdit && competencyQuery.error;
	const isArchived = isEdit && competencyQuery.data?.competency?.status === "archived";

	// On a failed edit load, bounce back to the list with an error toast.
	// `navigate` is a stable callback from useNavigate(); `loadError` is a stable
	// React Query result, so this fires exactly once before the component unmounts.
	useEffect(() => {
		if (loadError) {
			toast.error("Competency not found");
			navigate(COMPETENCIES_LIST_PATH);
		} else if (isArchived) {
			toast.error("Reopen this competency to edit it");
			navigate(COMPETENCIES_LIST_PATH);
		}
	}, [loadError, isArchived, navigate]);

	if (isLoading || loadError || isArchived) {
		return <div data-testid="competency-form-loading" />;
	}

	const initialValues: CompetencyFormValues =
		isEdit && competencyQuery.data?.competency
			? fromCompetency(competencyQuery.data.competency)
			: EMPTY_FORM_VALUES;

	const title = isEdit ? "Edit Competency" : "Create a Competency";
	const subtitle = isEdit ? "Edit Competency details and levels" : "Fill out this form to create a competency";

	return (
		<div className="flex justify-center px-6 py-8">
			<div className="w-full max-w-[560px] rounded-md bg-white p-8 shadow-sm">
				<div className="mb-6 flex flex-col items-center gap-1">
					<Text size="sub-heading" weight="semi-bold" color="neutral-9">
						{title}
					</Text>
					<Text size="paragraph-2" weight="regular" color="neutral-7">
						{subtitle}
					</Text>
				</div>

				<CompetencyForm
					mode={mode}
					competencyId={isEdit ? competencyId : undefined}
					initialValues={initialValues}
				/>
			</div>
		</div>
	);
};

export default CreateEditCompetency;
